import { api } from "@/lib/apiClient";
import { isAxiosError } from "axios";

export type SalaryIncrementType = "percentage" | "fixed";

export type SalaryIncrementTarget = "basic" | "allowance" | "deduction";

export type SalaryIncrementRecord = {
  id: string;
  employeeId: string;
  incrementBatchId: string | null;
  incrementTarget: SalaryIncrementTarget;
  allowanceId: string | null;
  allowanceLabel: string | null;
  deductionId: string | null;
  deductionLabel: string | null;
  salaryProcessedId: string | null;
  incrementType: SalaryIncrementType;
  incrementValue: string;
  oldSalary: string;
  newSalary: string;
  effectiveFrom: string;
  reason: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
};

type PaginatedResponse = {
  data?: Record<string, unknown>[];
  meta?: {
    current_page?: number;
    last_page?: number;
    per_page?: number;
    total?: number;
  };
};

function str(v: unknown): string {
  if (v == null || v === "") return "";
  return String(v);
}

function mapRow(raw: Record<string, unknown>): SalaryIncrementRecord {
  const t = str(raw.increment_type) === "percentage" ? "percentage" : "fixed";
  const targetRaw = str(raw.increment_target);
  const target: SalaryIncrementTarget = targetRaw === "allowance" ? "allowance" : targetRaw === "deduction" ? "deduction" : "basic";
  return {
    id: str(raw.id),
    employeeId: str(raw.employee_id),
    incrementBatchId: raw.increment_batch_id != null && str(raw.increment_batch_id) !== "" ? str(raw.increment_batch_id) : null,
    incrementTarget: target,
    allowanceId: raw.allowance_id != null ? str(raw.allowance_id) : null,
    allowanceLabel: raw.allowance_label != null ? str(raw.allowance_label) : null,
    deductionId: raw.deduction_id != null ? str(raw.deduction_id) : null,
    deductionLabel: raw.deduction_label != null ? str(raw.deduction_label) : null,
    salaryProcessedId: raw.salary_processed_id != null ? str(raw.salary_processed_id) : null,
    incrementType: t,
    incrementValue: str(raw.increment_value),
    oldSalary: str(raw.old_salary),
    newSalary: str(raw.new_salary),
    effectiveFrom: str(raw.effective_from).slice(0, 10),
    reason: raw.reason != null ? str(raw.reason) : null,
    createdBy: raw.created_by != null ? str(raw.created_by) : null,
    createdAt: str(raw.created_at),
    updatedAt: str(raw.updated_at),
  };
}

export function salaryIncrementApiErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const data = error.response?.data as { message?: string; errors?: Record<string, string[]> } | undefined;
    if (data?.errors) {
      const first = Object.values(data.errors).flat()[0];
      if (first) return first;
    }
    if (typeof data?.message === "string") return data.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return "Something went wrong.";
}

export async function listSalaryIncrements(params: {
  employeeId: string;
  page?: number;
  perPage?: number;
}): Promise<{ rows: SalaryIncrementRecord[]; meta: { currentPage: number; lastPage: number; perPage: number; total: number } }> {
  const response = await api.get<PaginatedResponse>(`/api/v1/employees/${encodeURIComponent(params.employeeId)}/salary-increments`, {
    params: { page: params.page, per_page: params.perPage },
  });
  const list = Array.isArray(response.data?.data) ? response.data.data : [];
  const meta = response.data?.meta ?? {};
  return {
    rows: list.map((r) => mapRow(r as Record<string, unknown>)),
    meta: {
      currentPage: meta.current_page ?? 1,
      lastPage: meta.last_page ?? 1,
      perPage: meta.per_page ?? params.perPage ?? 20,
      total: meta.total ?? list.length,
    },
  };
}

export async function listAllSalaryIncrements(params: {
  page?: number;
  perPage?: number;
}): Promise<{ rows: SalaryIncrementRecord[]; meta: { currentPage: number; lastPage: number; perPage: number; total: number } }> {
  const response = await api.get<PaginatedResponse>("/api/v1/salary-increments", {
    params: { page: params.page, per_page: params.perPage },
  });
  const list = Array.isArray(response.data?.data) ? response.data.data : [];
  const meta = response.data?.meta ?? {};
  return {
    rows: list.map((r) => mapRow(r as Record<string, unknown>)),
    meta: {
      currentPage: meta.current_page ?? 1,
      lastPage: meta.last_page ?? 1,
      perPage: meta.per_page ?? params.perPage ?? 20,
      total: meta.total ?? list.length,
    },
  };
}

export async function createSalaryIncrementBatch(params: {
  employeeId: string;
  effectiveFrom: string;
  reason?: string | null;
  basic: { incrementType: SalaryIncrementType; incrementValue: number | string } | null;
  allowances: Array<{ allowanceId: string; incrementType: SalaryIncrementType; incrementValue: number | string }>;
  deductions: Array<{ deductionId: string; incrementType: SalaryIncrementType; incrementValue: number | string }>;
}): Promise<{ records: SalaryIncrementRecord[]; message: string }> {
  const body: Record<string, unknown> = {
    effective_from: params.effectiveFrom,
    reason: params.reason?.trim() || null,
  };
  if (params.basic) {
    body.basic = {
      increment_type: params.basic.incrementType,
      increment_value: params.basic.incrementValue,
    };
  }
  if (params.allowances.length > 0) {
    body.allowances = params.allowances.map((a) => ({
      allowance_id: Number(a.allowanceId),
      increment_type: a.incrementType,
      increment_value: a.incrementValue,
    }));
  }
  if (params.deductions.length > 0) {
    body.deductions = params.deductions.map((d) => ({
      deduction_id: Number(d.deductionId),
      increment_type: d.incrementType,
      increment_value: d.incrementValue,
    }));
  }

  const response = await api.post<{ message?: string; data?: Record<string, unknown>[] }>(
    `/api/v1/employees/${encodeURIComponent(params.employeeId)}/salary-increments/batch`,
    body,
  );
  const list = Array.isArray(response.data?.data) ? response.data.data : [];
  return {
    records: list.map((r) => mapRow(r as Record<string, unknown>)),
    message: str(response.data?.message) || "Created.",
  };
}

export async function createSalaryIncrement(params: {
  employeeId: string;
  incrementTarget?: SalaryIncrementTarget;
  allowanceId?: string | null;
  deductionId?: string | null;
  incrementType: SalaryIncrementType;
  incrementValue: number | string;
  effectiveFrom: string;
  reason?: string;
}): Promise<{ record: SalaryIncrementRecord; message: string }> {
  const response = await api.post<{ message?: string; data?: Record<string, unknown> }>(
    `/api/v1/employees/${encodeURIComponent(params.employeeId)}/salary-increments`,
    {
      increment_target: params.incrementTarget ?? "basic",
      allowance_id: params.allowanceId != null && params.allowanceId !== "" ? Number(params.allowanceId) : null,
      deduction_id: params.deductionId != null && params.deductionId !== "" ? Number(params.deductionId) : null,
      increment_type: params.incrementType,
      increment_value: params.incrementValue,
      effective_from: params.effectiveFrom,
      reason: params.reason?.trim() || null,
    },
  );
  return {
    record: mapRow((response.data?.data ?? {}) as Record<string, unknown>),
    message: str(response.data?.message) || "Created.",
  };
}

export async function updateSalaryIncrement(params: {
  employeeId: string;
  id: string;
  patch: Partial<{
    incrementType: SalaryIncrementType;
    incrementValue: number | string;
    effectiveFrom: string;
    reason: string | null;
  }>;
}): Promise<{ record: SalaryIncrementRecord; message: string }> {
  const body: Record<string, unknown> = {};
  if (params.patch.incrementType !== undefined) body.increment_type = params.patch.incrementType;
  if (params.patch.incrementValue !== undefined) body.increment_value = params.patch.incrementValue;
  if (params.patch.effectiveFrom !== undefined) body.effective_from = params.patch.effectiveFrom;
  if (params.patch.reason !== undefined) body.reason = params.patch.reason?.trim() || null;

  const response = await api.patch<{ message?: string; data?: Record<string, unknown> }>(
    `/api/v1/employees/${encodeURIComponent(params.employeeId)}/salary-increments/${encodeURIComponent(params.id)}`,
    body,
  );
  return {
    record: mapRow((response.data?.data ?? {}) as Record<string, unknown>),
    message: str(response.data?.message) || "Saved.",
  };
}

export async function deleteSalaryIncrement(params: { employeeId: string; id: string }): Promise<{ message: string }> {
  const response = await api.delete<{ message?: string }>(
    `/api/v1/employees/${encodeURIComponent(params.employeeId)}/salary-increments/${encodeURIComponent(params.id)}`,
  );
  return { message: str(response.data?.message) || "Deleted." };
}
